import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { Square, Triangle } from '@phosphor-icons/react';
import { extractRepositoryOwnerAndRepo } from 'common/github/githubUtils';
import { parsePackageUrl } from 'common/stack/stackUtils';
import { ListItemFigure } from 'client/components/Other/ListItemFigure';

const DEFAULT_IMAGE =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMiIgaGVpZ2h0PSIzMiIgZmlsbD0iIzY2NTBGQSIgdmlld0JveD0iMCAwIDI1NiAyNTYiPjxwYXRoIGQ9Ik0yMjMuNjgsNjYuMTUsMTM1LjY4LDE4YTE1Ljg4LDE1Ljg4LDAsMCwwLTE1LjM2LDBsLTg4LDQ4LjE3YTE2LDE2LDAsMCwwLTguMzIsMTR2OTUuNjRhMTYsMTYsMCwwLDAsOC4zMiwxNGw4OCw0OC4xN2ExNS44OCwxNS44OCwwLDAsMCwxNS4zNiwwbDg4LTQ4LjE3YTE2LDE2LDAsMCwwLDguMzItMTRWODAuMThBMTYsMTYsMCwwLDAsMjIzLjY4LDY2LjE1Wk0xMjgsMzJsODAuMzQsNDQtMjkuNzcsMTYuMy04MC4zNS00NFpNMTI4LDEyMCw0Ny42Niw3NmwzMy45LTE4LjU2LDgwLjM0LDQ0Wk00MCw5MGw4MCw0My43OHY4NS43OUw0MCwxNzUuODJabTE3Niw4NS43OGgwbC04MCw0My43OVYxMzMuODJsMzItMTcuNTFWMTUyYTgsOCwwLDAsMCwxNiwwVjEwNy41NUwyMTYsOTB2ODUuNzdaIj48L3BhdGg+PC9zdmc+';

export const useComponentIcon = ({ url: urlProp }: { url?: string | null }) =>
  useMemo(() => {
    if (!urlProp) return undefined;

    const { url, ref = 'main' } = parsePackageUrl(urlProp);
    const { status: parseStatus, owner, repo } = extractRepositoryOwnerAndRepo(url);
    const imageUrl = parseStatus === 'ok' ? `https://diploi.b-cdn.net/component/${owner}/${repo}/icon.svg?ref=${ref}` : undefined;
    return imageUrl;
  }, [urlProp]);

export const ComponentIcon = ({
  url,
  type,
  size = 32,
  ornament: ornamentProp,
  ornamentSize,
  hideType,
}: {
  url: string | null;
  type: 'stack' | 'component' | 'addon';
  size?: number;
  ornament?: JSX.Element;
  ornamentSize?: number;
  hideType?: boolean;
}) => {
  const imageUrl = useComponentIcon({ url });
  const [workingImageUrl, setWorkingImageUrl] = useState(DEFAULT_IMAGE);
  const imageProps = useMemo(() => ({ src: workingImageUrl, alt: 'component icon' }), [workingImageUrl]);

  useEffect(() => {
    if (!imageUrl) return;

    const img = new Image();
    img.addEventListener('load', () => {
      setWorkingImageUrl(img.src);
    });
    img.src = imageUrl;

    return () => img.remove();
  }, [imageUrl]);

  const ornamentProps = useMemo(() => ({ size: ornamentSize ?? size - 16, weight: 'bold' as const }), [size, ornamentSize]);
  const ornament = useMemo(
    () => ornamentProp ?? (type === 'component' ? <Square {...ornamentProps} /> : <Triangle {...ornamentProps} />),
    [type, ornamentProp, ornamentProps]
  );

  return <ListItemFigure image={imageProps} ornament={hideType ? null : ornament} size={size} css={iconStyle} />;
};

const iconStyle = css`
  > img {
    position: relative;
  }
`;
